import type { FeedFilters } from '#services/api/mia.ts';

import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeScalableIcon } from '@mend/mui';
import { Box, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { appointmentTypes, providerDirectory } from '#lib/react-query/queries.ts';

/** How many selections a dimension names in the tooltip before collapsing the rest into "+N more". */
const maxNamedSelections = 3;

/**
 * Discloses that the Coordinate tab's numbers are filtered — the spec's "funnel affordance". A
 * funnel icon shown on the Daily Briefing and each bucket card only while at least one filter
 * dimension is active, so a filtered count is never mistaken for the whole practice. The
 * hover/focus tooltip names each active dimension. Informational only — no click action.
 * Renders nothing when no filter is active.
 */
export function ActiveFiltersBadge(props: { filters: FeedFilters }) {
  const providerIds = Array.isArray(props.filters.providerIds) ? props.filters.providerIds : [];
  const appointmentTypeIds = props.filters.appointmentTypeIds ?? [];
  const baseAppointmentTypes = props.filters.baseAppointmentTypes ?? [];

  const providerNames = useProviderNames(providerIds);
  const appointmentTypeNames = useAppointmentTypeNames(appointmentTypeIds);

  const dimensions = [
    { label: 'Providers', total: providerIds.length, names: providerNames },
    { label: 'Appointment types', total: appointmentTypeIds.length, names: appointmentTypeNames },
    {
      label: 'Base appointment types',
      total: baseAppointmentTypes.length,
      names: baseAppointmentTypes,
    },
  ]
    .filter((dimension) => dimension.total > 0)
    .map(formatDimension);

  if (dimensions.length === 0) {
    return null;
  }

  const disclosure = `Filters applied — ${dimensions.join('; ')}`;

  return (
    <Tooltip title={disclosure}>
      <Box
        component="span"
        tabIndex={0}
        aria-label={disclosure}
        sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'default' }}
      >
        <FontAwesomeScalableIcon icon={faFilter} fontSize="small" color="inherit" />
      </Box>
    </Tooltip>
  );
}

/**
 * The selected providers' display names, in selection order — `undefined` while the directory read
 * is in flight. Reads the Provider picker's directory, so the names are already in cache whenever
 * the filter bar has rendered.
 */
function useProviderNames(providerIds: number[]): string[] | undefined {
  const providersQuery = useQuery({
    ...providerDirectory,
    enabled: providerIds.length > 0,
  });

  if (!providersQuery.data) {
    return undefined;
  }

  const providersById = new Map(providersQuery.data.map((provider) => [provider.id, provider]));

  return providerIds.flatMap((id) => {
    const provider = providersById.get(id);
    return provider ? [provider.name] : [];
  });
}

/** The selected appointment types' names, from the picker's preloaded directory. */
function useAppointmentTypeNames(appointmentTypeIds: number[]): string[] | undefined {
  const appointmentTypesQuery = useQuery({
    ...appointmentTypes(),
    enabled: appointmentTypeIds.length > 0,
  });

  if (!appointmentTypesQuery.data) {
    return undefined;
  }

  return appointmentTypeIds.flatMap((id) => {
    const appointmentType = appointmentTypesQuery.data.find((type) => type.id === id);
    // The wire type allows numeric names; the disclosure always reads as text.
    return appointmentType ? [String(appointmentType.name)] : [];
  });
}

// Stale ids resolve to no name at all (a departed provider, a deleted type) — they still count
// toward the "+N more" remainder so the disclosure never understates how narrow the filter is.
// `names: undefined` means the directory read hasn't resolved yet — fall back to a bare count.
function formatDimension(dimension: {
  label: string;
  total: number;
  names: readonly string[] | undefined;
}): string {
  // An empty list means every selected id is stale, which reads the same as "not resolved yet".
  if (!dimension.names || dimension.names.length === 0) {
    return `${dimension.label}: ${dimension.total} selected`;
  }

  const named = dimension.names.slice(0, maxNamedSelections);
  const remainder = dimension.total - named.length;

  return `${dimension.label}: ${named.join(', ')}${remainder > 0 ? ` +${remainder} more` : ''}`;
}
